import { ReactNode } from 'react';

import { Box, Flex, Switch, Text } from '@valwin/facto/components';

const BORDER_WIDTH = '1px';

type Identity = {
  logo: ReactNode;
  name: string;
};

type SwitchProps = {
  key?: number;
  checked: boolean;
  onCheckedChange: () => void;
};

type PendingProps = {
  isPending: boolean;
  label?: string;
};

type IntegrationCardProps = {
  identity: Identity;
  switchProps: SwitchProps;
  pending?: PendingProps;
};

export function IntegrationCard({
  identity,
  switchProps,
  pending,
}: IntegrationCardProps) {
  return (
    <Box
      position="relative"
      borderRadius="md"
      padding={BORDER_WIDTH}
      overflow="hidden"
      minWidth="64"
      minHeight="28"
    >
      <Box
        position="absolute"
        inset="0"
        borderRadius="md"
        borderWidth={BORDER_WIDTH}
        borderColor="border"
        pointerEvents="none"
      />

      {pending?.isPending && (
        <Box
          position="absolute"
          inset="-100%"
          background="conic-gradient(from 0deg, transparent 0deg, var(--chakra-colors-brand-solid) 45deg, transparent 90deg, transparent 180deg, var(--chakra-colors-brand-solid) 225deg, transparent 270deg, transparent 360deg)"
          animation="spin 1.5s linear infinite"
        />
      )}

      <Flex
        position="relative"
        justify="space-between"
        align="flex-start"
        padding="4"
        borderRadius={`calc(var(--chakra-radii-md) - ${BORDER_WIDTH})`}
        background="bg"
        height="100%"
      >
        <Flex direction="column" gap="1">
          <Box>{identity.logo}</Box>
          <Box textStyle="sm">{identity.name}</Box>
        </Flex>

        {/* key forces a remount to reset Ark UI's internal state machine (hover/focus)
            which can get stuck when a popup steals and returns focus */}
        <Switch
          key={switchProps.key}
          checked={switchProps.checked}
          onCheckedChange={switchProps.onCheckedChange}
        />

        {pending?.isPending && pending.label && (
          <Flex
            position="absolute"
            inset="0"
            align="center"
            justify="center"
            borderRadius={`calc(var(--chakra-radii-md) - ${BORDER_WIDTH})`}
            background="whiteAlpha.800"
          >
            <Text textStyle="sm" fontWeight="medium" color="gray.500">
              {pending.label}
            </Text>
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
